import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useAccounts } from '../lib/useAccounts';
import AccountDrawer from '../components/AccountDrawer';
import { shortLocation, type AccountType } from '../lib/accounts';

type Filter = 'all' | AccountType;

export default function Accounts() {
  const { demo } = useAuth();
  const { data, loading } = useAccounts(demo);
  const [params, setParams] = useSearchParams();

  const filter = (params.get('type') as Filter) || 'all';
  const q = params.get('q') || '';
  const openId = params.get('open');

  const setFilter = (f: Filter) => {
    const p = new URLSearchParams(params);
    if (f === 'all') p.delete('type'); else p.set('type', f);
    setParams(p, { replace: true });
  };
  const setQ = (v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set('q', v); else p.delete('q');
    setParams(p, { replace: true });
  };
  const open = (id: string | null) => {
    const p = new URLSearchParams(params);
    if (id) p.set('open', id); else p.delete('open');
    setParams(p, { replace: true });
  };

  const rows = useMemo(() => {
    let a = data?.accounts ?? [];
    if (filter !== 'all') a = a.filter((x) => x.type === filter || x.type === 'both');
    if (q.trim()) {
      const t = q.toLowerCase();
      a = a.filter((x) =>
        x.name.toLowerCase().includes(t) ||
        (x.billing?.city || '').toLowerCase().includes(t) ||
        (x.billing?.state || '').toLowerCase().includes(t) ||
        (x.category || '').toLowerCase().includes(t));
    }
    return a;
  }, [data, filter, q]);

  const active = data?.accounts.find((x) => x.id === openId) || null;

  if (loading) return <div className="loading"><div className="spinner" /> Loading accounts…</div>;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-title">Accounts</div>
          <div className="page-sub">{rows.length} of {data?.accounts.length ?? 0} · clients &amp; vendors in one place</div>
        </div>
        {data && (data.live
          ? <span className="live-flag">Live · {data.sources.join(' + ')}</span>
          : <span className="demo-flag">Demo data</span>)}
      </div>

      <div className="toolbar">
        <input className="search" placeholder="Search name, city, state, category…" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="seg">
          {(['all', 'client', 'vendor', 'both'] as Filter[]).map((f) => (
            <button key={f} className={filter === f ? 'on' : ''} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : f[0].toUpperCase() + f.slice(1) + (f === 'both' ? '' : 's')}
            </button>
          ))}
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>Account</th><th>Type</th><th>Location</th><th>Detail</th><th>Status</th></tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} onClick={() => open(a.id)}>
                <td>
                  <div className="acct-name">{a.name}</div>
                  {a.source && <div className="acct-meta">{a.source}</div>}
                </td>
                <td><span className={`pill pill-${a.type}`}>{a.type}</span></td>
                <td>{shortLocation(a)}</td>
                <td style={{ color: 'var(--tx2)' }}>{a.type === 'vendor' ? (a.category || '—') : (a.industry || '—')}</td>
                <td><span className={`pill pill-${a.status}`}>{a.status}</span></td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5}><div className="empty">No accounts match your filters.</div></td></tr>
            )}
          </tbody>
        </table>
      </div>

      {active && <AccountDrawer account={active} onClose={() => open(null)} />}
    </>
  );
}
